// @flow
import React, { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import * as StringUtil from 'app/common/lib/StringUtil';
import AccountSwitcherModal from 'app/common/react/components/growth/unauth/signup/AccountSwitcher/AccountSwitcherModal';
import DelightfulBubbles from 'app/common/react/components/growth/activation/animations/DelightfulBubbles/DelightfulBubbles';
import i18n from 'app/common/lib/i18n';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { Dispatch } from 'redux';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import { getNameComponents } from 'app/common/lib/ActivationUtil';
import { track } from 'app/common/lib/react/analytics';
import setNuxProgress, { type NuxStep } from 'app/common/redux/actions/nuxProgress';
import {
  Box,
  Button,
  Heading,
  Icon,
  IconButton,
  Label,
  Link,
  Text,
  TextField,
  Touchable,
} from 'gestalt';
import {
  COMPLETED,
  NUX_WELCOME_NAME_STEP_STATUS,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { updateUser, setUserState } from 'app/common/redux/actions/nux/updateUser';

type OwnProps = {|
  incrementNuxStep: () => void,
  personalizationCopy: {
    headerText: string,
    subHeaderText: string,
  },
|};

type DispatchProps = {|
  dispatchSetNuxStepProgress: (NuxStep) => void,
  updateNameInfo: ({
    full_name?: string,
    first_name?: string,
    last_name?: string,
  }) => Promise<any>,
  setNuxNameProgress: () => void,
|};

type ReduxStateProps = {|
  firstName: string,
  userEmail: string,
|};

type Props = {| ...OwnProps, ...DispatchProps, ...ReduxStateProps |};

type State = {|
  name: string,
  showAccountSwitchModal: boolean,
  showNameInput: boolean,
  showNoNameError: boolean,
  showNameLengthError: boolean,
|};

const TEXTFIELD_WIDTH = 250; // fix width to prevent resizing from error message

class NuxWelcomeNameStep extends PureComponent<Props, State> {
  state: State = {
    name: this.props.firstName,
    showAccountSwitchModal: false,
    showNameInput: false,
    showNoNameError: false,
    showNameLengthError: false,
  };

  componentDidMount() {
    this.props.dispatchSetNuxStepProgress('welcome');

    track(PCONST.thrift.EventType.VIEW, {
      view: PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_WELCOME,
    });
  }

  onNameChange = ({ value: name }: Object) => {
    this.setState({
      name: name || '',
      showNoNameError: !name || StringUtil.isEmpty(name),
      showNameLengthError: !!name && name.length > 30,
    });
  };

  toggleNameInput = () => {
    const setStateToggleCb = ({ showNameLengthError, showNoNameError, showNameInput }) => ({
      showNameInput: showNameLengthError || showNoNameError || !showNameInput,
    });

    // Close input field only if no error is showing
    this.setState(setStateToggleCb);

    track(PCONST.thrift.EventType.CLICK, {
      element: PCONST.thrift.ElementType.NAME_BUTTON,
      view: PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_WELCOME,
    });
  };

  continueAndSave = () => {
    const { incrementNuxStep, setNuxNameProgress, updateNameInfo } = this.props;
    const { name } = this.state;

    const nameData = getNameComponents(name);
    updateNameInfo(nameData).then(() => {
      setNuxNameProgress();
      track(PCONST.thrift.EventType.CLICK, {
        element: PCONST.thrift.ElementType.NEXT_BUTTON,
        view: PCONST.thrift.ViewType.ORIENTATION,
        viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_WELCOME,
      });

      incrementNuxStep();
    });
  };

  getError = () => {
    const { showNameLengthError, showNoNameError } = this.state;
    let error = '';
    if (showNoNameError) {
      error = i18n._('And your name is...?', "Error flyout when user doesn't input name");
    } else if (showNameLengthError) {
      error = i18n._(
        "Hmm, that's a few characters too long. Try a nickname instead?",
        'Name exceeds 30 character maximum error',
      );
    }
    return error;
  };

  handleHideAccountSwitcher = () => this.setState({ showAccountSwitchModal: false });

  handleNotYouClick = (e: {
    event: SyntheticMouseEvent<HTMLDivElement> | SyntheticKeyboardEvent<HTMLDivElement>,
  }) => {
    e.event.preventDefault();
    trackRegisterAction('web.nux.click_login');
    this.setState({
      showAccountSwitchModal: true,
    });
  };

  render() {
    const {
      personalizationCopy: { headerText, subHeaderText },
      userEmail,
    } = this.props;
    const { name, showAccountSwitchModal, showNameInput } = this.state;
    const disabledInputCopy = StringUtil.dangerouslyFormat(
      i18n._('{{ titleText }}, {{ name }}!', 'Greeting with inferred name.'),
      { titleText: headerText, name },
    );
    const enabledInputCopy = headerText;

    const notYouText = i18n._(
      'Already have a Pinterest account?',
      'subtitle to ask users who create a new account if they meant to log in to an existing account',
    );

    const error = this.getError();

    const nameEditIcon = (
      <Box data-test-id="nux-edit-name-btn" marginStart={1}>
        <IconButton
          accessibilityLabel="editName"
          bgColor="transparent"
          icon={showNameInput ? 'check' : 'edit'}
          onClick={this.toggleNameInput}
          size="sm"
        />
      </Box>
    );
    return (
      <Fragment>
        {showAccountSwitchModal && (
          <AccountSwitcherModal
            onDismiss={this.handleHideAccountSwitcher}
            showAddAccount
            hideAccountSwitchValueProp
            customTaglineText={i18n._(
              'Log in',
              'Title on modal for users to log in to an existing account after they accidentally created a new account',
            )}
          />
        )}
        <Box direction="column" display="flex" height="100%" padding={10} minWidth="100%">
          <Box
            alignItems="center"
            direction="column"
            display="flex"
            flex="grow"
            justifyContent="around"
            marginBottom={10}
            width="100%"
          >
            <DelightfulBubbles
              delayOffset={-5 /** negative so some bubbles start on screen**/}
              xRadiusAdjustment={40}
            >
              <Box
                alignItems="center"
                color="lightWash"
                direction="column"
                display="flex"
                height={100}
                justifyContent="end"
                overflow="hidden"
                shape="circle"
                width={100}
              >
                <Icon
                  accessibilityLabel={i18n._('Loading state for any sign up registration')}
                  color="gray"
                  icon="person"
                  size={70}
                />
              </Box>
            </DelightfulBubbles>
            <Box alignItems="center" marginTop={5}>
              <Text align="center" size="lg" truncate>
                {userEmail}
              </Text>
            </Box>
            <Box alignItems="end" display="flex" width="100%">
              <Box
                dangerouslySetInlineStyle={{ __style: { textAlign: 'center' } }}
                direction="column"
                display="flex"
                marginStart={9}
                maxHeight={170 /*limit name to three lines*/}
                overflow="hidden"
                width="100%"
              >
                <Label htmlFor={'name'}>
                  <Heading size="sm">
                    {showNameInput ? enabledInputCopy : disabledInputCopy}
                  </Heading>
                </Label>
                {showNameInput && (
                  <Box
                    alignItems="center"
                    data-test-id="nux-edit-name-input"
                    display="flex"
                    justifyContent="center"
                    padding={1}
                  >
                    <Box width={TEXTFIELD_WIDTH}>
                      <TextField
                        id="name"
                        errorMessage={error}
                        onChange={this.onNameChange}
                        placeholder={i18n._('name')}
                        type="text"
                        value={name}
                      />
                    </Box>
                    {nameEditIcon}
                  </Box>
                )}
              </Box>
              {!showNameInput && nameEditIcon}
            </Box>
            <Box width="100%">
              <Text align="center" size="xl">
                {subHeaderText}
              </Text>
            </Box>
          </Box>
          <Box data-test-id="nux_name_done_btn">
            <Button
              accessibilityLabel={i18n._('Next', 'go to next nux step')}
              color="red"
              disabled={!!error}
              onClick={this.continueAndSave}
              text={i18n._('Next', 'next nux step button')}
              type="button"
            />
          </Box>

          <Box marginTop={4}>
            <Touchable onTouch={this.handleNotYouClick} fullWidth={false}>
              <Text size="lg" align="center">
                {notYouText}
              </Text>
              <Text align="center" size="lg" weight="bold">
                <Link href={'#'}>
                  {i18n._(
                    'Log in instead',
                    'Link allowing users who just created a new account the option to log in instead into an existing account',
                  )}
                </Link>
              </Text>
            </Touchable>
          </Box>
        </Box>
      </Fragment>
    );
  }
}

const mapStateToProps = ({ viewer }: ReduxState): ReduxStateProps => ({
  firstName: viewer.firstName ? viewer.firstName : '',
  userEmail: viewer.isAuth ? viewer.email : '',
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  dispatchSetNuxStepProgress: (step: NuxStep) => dispatch(setNuxProgress(step)),
  updateNameInfo: (nameData) => dispatch(updateUser({ updateUserData: nameData })),
  setNuxNameProgress: () => dispatch(setUserState(NUX_WELCOME_NAME_STEP_STATUS, COMPLETED)),
});

export default connect<Props, OwnProps, ReduxStateProps, DispatchProps, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(NuxWelcomeNameStep);
